import React, { useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Add, DeleteOutline, FormatAlignCenter, FormatAlignLeft, FormatAlignRight } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import {
  CERTIFICATE_FONTS,
  CertificateDesign,
  CertificateField,
  FIELD_LABELS,
  FieldAlign,
  IMAGE_FIELDS,
  newField,
} from './certificate-model';
import { useCertificateAssetUpload } from './useCertificateAssetUpload';
import { importCertificatePdf } from './pdf-import';

const PRIMARY = '#796FFF';

const useStyles = makeStyles(() =>
  createStyles({
    section: { marginBottom: '24px' },
    label: { fontSize: '14px', fontWeight: 500, color: '#545454', marginBottom: '8px' },
    helper: { fontSize: '12px', color: '#808080', marginTop: '6px' },
    background: { display: 'flex', alignItems: 'center', gap: '8px' },
    thumb: { width: '72px', height: '51px', objectFit: 'cover', border: '1px solid #EFEFEF', borderRadius: '4px' },
    fieldRow: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      padding: '8px 10px',
      borderRadius: '6px',
      border: '1px solid #EFEFEF',
      marginBottom: '6px',
      fontSize: '14px',
      justifyContent: 'space-between',
      fontFamily: 'inherit',
    },
    fieldSelected: { borderColor: PRIMARY, boxShadow: `0 0 0 1px ${PRIMARY}` },
    editor: { background: '#FAFAFA', borderRadius: '6px', padding: '12px', marginTop: '8px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' },
    inline: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' },
    colour: { width: '40px', height: '32px', border: '1px solid #DCDCDC', borderRadius: '4px', padding: 0 },
  })
);

interface Props {
  design: CertificateDesign;
  selectedId?: string;
  onSelect: (id?: string) => void;
  onChange: (patch: Partial<CertificateDesign>) => void;
  onChangeField: (id: string, patch: Partial<CertificateField>) => void;
}

export const CustomFieldsPanel = ({ design, selectedId, onSelect, onChange, onChangeField }: Props) => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const { upload, uploading } = useCertificateAssetUpload();
  const fileRef = useRef<HTMLInputElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [importing, setImporting] = useState(false);
  const selected = design.fields.find((field) => field.id === selectedId);

  const onFile = async (file?: File) => {
    if (!file) return;
    try {
      if (file.type === 'application/pdf') {
        setImporting(true);
        const imported = await importCertificatePdf(file);
        const background = await upload(
          'background',
          new File([imported.background], 'design.png', { type: 'image/png' })
        );
        onChange({
          background,
          orientation: imported.orientation,
          fields: [...design.fields, ...imported.fields],
        });
        setSnackbar({
          open: true,
          variant: 'success',
          message: imported.fields.length
            ? `Found ${imported.fields.length} merge tag${imported.fields.length === 1 ? '' : 's'}`
            : 'No merge tags found. Add fields to place the details.',
        });
        return;
      }
      onChange({ background: await upload('background', file) });
    } catch (e) {
      setSnackbar({ open: true, variant: 'error', message: (e as Error).message || 'Unable to read that file' });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addField = (key: string) => {
    const field = newField(key, design.orientation);
    onChange({ fields: [...design.fields, field] });
    onSelect(field.id);
    setMenuAnchor(null);
  };

  const removeField = (id: string) => {
    onChange({ fields: design.fields.filter((field) => field.id !== id) });
    onSelect(undefined);
  };

  const alignIcons: Record<FieldAlign, React.ReactNode> = {
    left: <FormatAlignLeft fontSize='small' />,
    center: <FormatAlignCenter fontSize='small' />,
    right: <FormatAlignRight fontSize='small' />,
  };

  return (
    <>
      <Box className={classes.section}>
        <Typography className={classes.label}>Background</Typography>
        <Box className={classes.background}>
          {design.background && <img className={classes.thumb} src={design.background.url} alt='Background' />}
          <TabletButton
            variant='outlined'
            color='primary'
            disabled={uploading || importing}
            onClick={() => fileRef.current?.click()}
          >
            {importing ? 'Reading PDF' : design.background ? 'Replace' : 'Upload design'}
          </TabletButton>
          {design.background && (
            <TabletButton variant='text' color='error' onClick={() => onChange({ background: null })}>
              Remove
            </TabletButton>
          )}
        </Box>
        <Typography className={classes.helper}>
          An A4 PDF, PNG or JPG of your certificate. In a PDF, type merge tags like {'{{VOLUNTEER_NAME}}'} where the
          details go and they become fields automatically.
        </Typography>
        <input
          ref={fileRef}
          type='file'
          accept='image/png,image/jpeg,application/pdf'
          hidden
          onChange={(e) => onFile(e.target.files?.[0])}
        />
      </Box>

      <Box className={classes.section}>
        <Typography className={classes.label}>Fields</Typography>
        {design.fields.map((field) => (
          <ButtonBase
            key={field.id}
            className={`${classes.fieldRow} ${field.id === selectedId ? classes.fieldSelected : ''}`}
            onClick={() => onSelect(field.id)}
          >
            <span>{field.key === 'TEXT' ? field.text || FIELD_LABELS.TEXT : FIELD_LABELS[field.key]}</span>
            <IconButton
              size='small'
              aria-label='Remove field'
              onClick={(e) => {
                e.stopPropagation();
                removeField(field.id);
              }}
            >
              <DeleteOutline fontSize='small' />
            </IconButton>
          </ButtonBase>
        ))}
        <TabletButton
          variant='text'
          color='primary'
          startIcon={<Add />}
          onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)}
        >
          Add field
        </TabletButton>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          {Object.keys(FIELD_LABELS).map((key) => (
            <MenuItem key={key} onClick={() => addField(key)}>
              {FIELD_LABELS[key]}
            </MenuItem>
          ))}
        </Menu>
        <Typography className={classes.helper}>Drag fields on the page to position them.</Typography>

        {selected && (
          <Box className={classes.editor}>
            {selected.key === 'TEXT' && (
              <TextField
                fullWidth
                variant='outlined'
                size='small'
                label='Text'
                value={selected.text}
                helperText='Placeholders like {{VOLUNTEER_NAME}} work here too'
                onChange={(e) => onChangeField(selected.id, { text: e.target.value })}
              />
            )}
            {IMAGE_FIELDS.includes(selected.key) ? (
              <TextField
                fullWidth
                variant='outlined'
                size='small'
                type='number'
                label='Size (mm)'
                value={selected.width}
                onChange={(e) => onChangeField(selected.id, { width: Number(e.target.value) || 5 })}
              />
            ) : (
              <>
                <Box className={classes.grid}>
                  <FormControl variant='outlined' size='small'>
                    <InputLabel shrink>Font</InputLabel>
                    <Select
                      value={selected.font}
                      onChange={(e) => onChangeField(selected.id, { font: e.target.value as string })}
                      input={<OutlinedInput notched label='Font' />}
                    >
                      {CERTIFICATE_FONTS.map((font) => (
                        <MenuItem key={font.key} value={font.key}>
                          {font.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    variant='outlined'
                    size='small'
                    type='number'
                    label='Size (pt)'
                    value={selected.size}
                    onChange={(e) => onChangeField(selected.id, { size: Number(e.target.value) || 6 })}
                  />
                </Box>
                <Box className={classes.inline}>
                  <Box display='flex' alignItems='center' style={{ gap: '4px' }}>
                    {(['left', 'center', 'right'] as FieldAlign[]).map((align) => (
                      <IconButton
                        key={align}
                        size='small'
                        aria-label={`Align ${align}`}
                        color={selected.align === align ? 'primary' : 'default'}
                        onClick={() => onChangeField(selected.id, { align })}
                      >
                        {alignIcons[align]}
                      </IconButton>
                    ))}
                    <input
                      type='color'
                      aria-label='Text colour'
                      className={classes.colour}
                      value={selected.colour}
                      onChange={(e) => onChangeField(selected.id, { colour: e.target.value })}
                    />
                  </Box>
                  <Box display='flex' alignItems='center'>
                    <Typography style={{ fontSize: 14 }}>Bold</Typography>
                    <Switch
                      color='primary'
                      size='small'
                      checked={selected.bold}
                      onChange={(e) => onChangeField(selected.id, { bold: e.target.checked })}
                    />
                  </Box>
                </Box>
                <Typography className={classes.helper}>Long names shrink to fit the box width.</Typography>
              </>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
